import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar } from 'react-date-range';

import {
  getCreatorMonthDiaryDates,
  getThisMonthDiaryDates,
} from '../api/diary';
import { SimpleDiary } from '../api/diary/dto/get-creator-month-diary-dates.res.dto';
import LoadingPage from '../Member/LoadingPage';
import {
  BRAND_COLOR_1,
  GRAY_COLOR_800,
  handleError,
  s3CloudFrontUrlEndpoint,
} from '../utils/common';
import { formattedDate } from '../utils/date/formattedDate';
import { useAlertModal } from '../utils/hook/hook';

const arrowRightDisabled = `${s3CloudFrontUrlEndpoint}/svg/arrow-left-disabled_qBZDMgfQB.svg`;
const arrowRightActive = `${s3CloudFrontUrlEndpoint}/svg/arrow-left_Hm767HmG7.svg`;
const arrowLeftActive = `${s3CloudFrontUrlEndpoint}/svg/arrow-right_gJNPYiSXQ.svg`;

interface DiaryCalendarProps {
  year: number;
  month: number;
  setYear: (year: number) => void;
  setMonth: (month: number) => void;
  onClickDate: (date: Date) => void;
  /** 연도와 월을 동시에 변경할 때 사용 (연말/연초 이동 시) */
  setYearMonth: (year: number, month: number) => void;

  /** 다이어리 생성/수정시 creatorName 없음 */
  creatorName?: string;
}

const DiaryCalendar = ({
  year,
  month,
  setYear,
  setMonth,
  onClickDate,
  setYearMonth,
  creatorName,
}: DiaryCalendarProps) => {
  const [date, setDate] = useState(new Date());
  const { openAlertModal } = useAlertModal();

  const isDisabledNextButton = useMemo(
    () =>
      year === new Date().getFullYear() && month === new Date().getMonth() + 1,
    [year, month],
  );
  const isCreateDiary = !!creatorName;

  const isDiaryArray = (
    thisMonthDates: (string | SimpleDiary)[],
  ): thisMonthDates is SimpleDiary[] => !!creatorName;

  const getMappedDates = (thisMonthDates: SimpleDiary[]) =>
    thisMonthDates.map(({ cookDate }) => formattedDate(cookDate, 'yyyy-MM-dd'));

  // response별로 가공해서 uniq로 중복을 없애준다
  const select = (thisMonthDates: (string | SimpleDiary)[]) => {
    const filteredData = isDiaryArray(thisMonthDates)
      ? getMappedDates(thisMonthDates)
      : (thisMonthDates as string[]);

    return uniq(filteredData);
  };

  const { data: thisMonthDates = [], isLoading } = useQuery<
    (string | SimpleDiary)[],
    unknown,
    string[]
  >({
    queryKey: creatorName
      ? ['getCreatorMonthDiaryDates', creatorName, year, month]
      : ['getThisMonthDiaryDates', year, month],
    queryFn: creatorName
      ? () => getCreatorMonthDiaryDates(creatorName, year, month)
      : () => getThisMonthDiaryDates(year, month),
    meta: {
      onError: (e: unknown) =>
        handleError(openAlertModal, e, '집밥일기 달력 가져오기'),
    },
    select,
  });

  const isClickableDate = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');

    return thisMonthDates ? thisMonthDates.includes(formattedDate) : false;
  };

  const _onClickDate = (date: Date) =>
    isCreateDiary
      ? isClickableDate(date) && onClickDate(date)
      : onClickDate(date);

  const handlePrevMonth = useCallback(() => {
    if (month === 1) {
      // 연도와 월을 동시에 변경
      setYearMonth(year - 1, 12);
    } else {
      setMonth(month - 1);
    }
  }, [year, month, setMonth, setYearMonth]);

  const handleNextMonth = useCallback(() => {
    if (isDisabledNextButton) {
      return;
    }
    if (month === 12) {
      // 연도와 월을 동시에 변경
      setYearMonth(year + 1, 1);
    } else {
      setMonth(month + 1);
    }
  }, [year, month, setMonth, setYearMonth, isDisabledNextButton]);

  const navigatorRenderer = useCallback(
    () => (
      <div className="flex items-center justify-between pb-16px">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="h-24px w-24px bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${arrowLeftActive})`,
          }}
          aria-label="이전 달"
        />
        <span
          className="text-16px font-medium"
          style={{ color: GRAY_COLOR_800 }}
        >
          {year}년 {month}월
        </span>
        <button
          type="button"
          onClick={handleNextMonth}
          disabled={isDisabledNextButton}
          className="h-24px w-24px bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${
              isDisabledNextButton ? arrowRightDisabled : arrowRightActive
            })`,
          }}
          aria-label="다음 달"
        />
      </div>
    ),
    [handlePrevMonth, handleNextMonth, year, month, isDisabledNextButton],
  );

  useEffect(() => {
    setDate(
      new Date(`${year}-${month < 10 ? `0${month}` : month}-01T00:00:00.000Z`),
    );
  }, [year, month]);

  return isLoading ? (
    <LoadingPage height={338} />
  ) : (
    <div>
      <Calendar
        locale={ko}
        date={date}
        shownDate={date}
        maxDate={new Date()}
        onChange={_onClickDate}
        navigatorRenderer={navigatorRenderer}
        dayContentRenderer={(date) => (
          <CustomDayContent
            date={date}
            isClickableDate={isClickableDate(date)}
            isCreateDiary={isCreateDiary}
          />
        )}
        color={BRAND_COLOR_1}
        showMonthAndYearPickers={false}
      />
    </div>
  );
};

export default DiaryCalendar;

function CustomDayContent({
  date,
  isClickableDate,
  isCreateDiary,
}: {
  date: Date;
  isClickableDate: boolean;
  isCreateDiary: boolean;
}) {
  return (
    <div
      className={`flex-center h-40px w-40px rounded-full ${
        isClickableDate
          ? 'bg-primary-100'
          : isCreateDiary
            ? ''
            : 'cursor-default'
      } `}
    >
      <span className="">{format(date, 'd')}</span>
    </div>
  );
}
